<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import NumberAnimation from "vue-number-animation";
import { getBuildingEcmFunctions, severityCode } from "@/services/api/chart.api";
import { getPartyEnergyEfficiencyObligatory } from "@/services/api/project.api";
import { useUserStore } from "@/stores/user";
import ChartBuildingFunctions from "@/components/app/charts/ChartBuildingFunctions.vue";
import { useToast } from "vue-toastification";
import { formatInt } from "@/composables/formatNumbers";

const toaster = useToast();

const userStore = useUserStore();

const props = defineProps({
  token: {
    type: String,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const buildingFunctions = ref({});
const percentageOfInformationObligation = ref(0);
const isLoadingFunction = ref(true);
const isLoadingObligation = ref(true);

const dataLoaded = computed(() => !isLoadingFunction.value && !isLoadingObligation.value);

watch(
  () => props.token,
  () => {
    if (props.token) {
      loadFunctions();
      loadObligation();
    } else {
      buildingFunctions.value = {};
    }
  },
  { immediate: true }
);

async function loadFunctions() {
  isLoadingFunction.value = true;
  const response = await getBuildingEcmFunctions(props.token);
  console.log("Buildingfunctions loadFunctions ", response);
  if (response.severity <= severityCode.warning) {
    buildingFunctions.value = response.data;
  } else {
    toaster.error("Ophalen van buildingFunctions is mislukt! ", response.message);
  }
  isLoadingFunction.value = false;
}

async function loadObligation() {
  isLoadingObligation.value = true;
  const response = await getPartyEnergyEfficiencyObligatory(userStore.currentPartyReference);
  console.log("Buildingfunctions loadObligation ", response);
  if (response.severity <= severityCode.warning) {
    percentageOfInformationObligation.value = Math.trunc((100 * response.data.mandatory) / response.data.total);
  } else {
    toaster.error("Ophalen van informatieplicht is mislukt! ", response.message);
  }
  isLoadingObligation.value = false;
}
</script>

<template>
  <div class="widget-item" :class="{ 'widget-item--compact': props.compact }">
    <header>
      <h2>Overzicht panden</h2>
      <div class="widget-actions">
        <router-link v-if="!props.compact" :to="{ name: 'BuildingOverview', params: { partyReference: userStore.currentPartyReference } }" class="widget-link">Bekijk totale overzicht</router-link>
        <information-dropdown id="overzicht-panden">
          <template v-slot:header>
            <h2>Gebouwenoverzicht</h2>
          </template>
          <p>
            Uw portefeuille kan bestaan uit verschillende type gebouwen. We tonen hier de verdeling van uw gebouwen op basis van de functie die het heeft. De functies die we gebruiken zijn gebaseerd
            op de bedrijfstakken die RVO gebruikt voor de informatieplicht.
            <br /><br />
          </p>
          <h4>Informatieplicht</h4>
          <p>
            Hoeveel van uw gebouwen zijn informatieplichtig? Het percentage van uw informatieplichtige gebouwen wordt hier getoond. Niet al uw gebouwen zitten mogelijk boven de norm van de
            informatieplicht van 50.000 kWh elektriciteit of 25.000 m³ aardgas(equivalent).
          </p>
        </information-dropdown>
      </div>
    </header>
    <loader-spinner v-if="!dataLoaded" :is-loading="true"></loader-spinner>
    <div v-else class="widget-wrapper" :min-height="200">
      <div v-if="props.compact" class="compact-content">
        <div class="widget-info">
          <h3>
            Informatieplichtig
            <span> <number-animation :to="percentageOfInformationObligation" :duration="1.6" :format="formatInt"></number-animation>% </span>
          </h3>
          <span class="obligation-text">Verbruikt uw gebouw per jaar meer dan 50.000 kWh elektriciteit of 25.000 m³ aardgas(equivalent)?</span>
        </div>
        <div class="widget-chart building-measures">
          <chart-building-functions key="doughnutChartBuildingFunctions" chartName="buildingFunctions" :radius="130" :sourceData="buildingFunctions"></chart-building-functions>
        </div>
      </div>
      <div v-else>
        <div class="widget-chart">
          <chart-building-functions legend-position-bottom key="doughnutChartBuildingFunctions" chartName="buildingFunctions" :radius="200" :sourceData="buildingFunctions"></chart-building-functions>
        </div>
        <div class="widget-info">
          <h3>
            Informatieplichtig
            <span> <number-animation :to="percentageOfInformationObligation" :duration="1.6" :format="formatInt"></number-animation>% </span>
          </h3>
          <span>Verbruikt uw gebouw per jaar meer dan 50.000 kWh elektriciteit of 25.000 m³ aardgas(equivalent)?</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-item--compact .widget-wrapper {
  flex: 1;
  min-height: 0;
  margin-top: 4px;
}

.widget-item--compact .compact-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
}

.widget-item--compact .widget-info {
  flex: 0 0 auto;
  margin-top: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}

.widget-item--compact .widget-info h3 {
  margin-bottom: 2px;
  min-height: auto;
  font-size: 0.9rem;
  line-height: 1.1;
}

.widget-item--compact .widget-info h3 span {
  font-size: 0.9rem;
  line-height: 1.1;
}

.widget-item--compact .obligation-text {
  display: block;
  font-size: 0.78rem;
  line-height: 1.15;
  color: #666;
}

.widget-item--compact .widget-chart.building-measures {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
}

.widget-item--compact .widget-chart.building-measures :deep(.vue-chart) {
  width: 100%;
}

.widget-item--compact .widget-chart.building-measures :deep(.advanced-doughnut) {
  flex-wrap: nowrap;
  align-items: flex-start;
}

.widget-item--compact .widget-chart.building-measures :deep(.doughnut) {
  flex: 0 0 auto;
}

.widget-item--compact .widget-chart.building-measures :deep(.vue-chart-legend) {
  margin: 4px;
  width: 156px;
}

.widget-item--compact .widget-chart.building-measures :deep(.vue-chart-legend li) {
  min-width: 0;
  margin-bottom: 3px;
}

.widget-item--compact .widget-chart.building-measures :deep(.chart-legend-label-text) {
  font-size: 0.72rem;
  line-height: 1.1;
}

.widget-item--compact .widget-chart.building-measures :deep(.chart-percentage) {
  font-size: 0.72rem;
}

:deep(.vue-chart .vue-chart-legend) {
  font-size: 10pt;
  margin-top: -5px;
}
</style>
